import { CartItem, Product } from '../types';
import { isProductOutOfStock } from './productAvailability';

export interface AutomaticPromotionDefinition {
    id_promocion: number;
    nombre: string;
    precio: number;
    producto: Product;
    items: { id_producto: number; cantidad: number }[];
}

interface AutomaticPromotionResult {
    cart: CartItem[];
    appliedPromotionNames: string[];
}

const normalizeRequirements = (items: AutomaticPromotionDefinition['items']) => {
    const requirements = new Map<number, number>();
    items.forEach(item => {
        const productId = Number(item.id_producto);
        const quantity = Number(item.cantidad);
        if (productId > 0 && quantity > 0) {
            requirements.set(productId, (requirements.get(productId) || 0) + quantity);
        }
    });
    return Array.from(requirements.entries()).map(([id_producto, cantidad]) => ({ id_producto, cantidad }));
};

export const applyAutomaticPromotions = (
    currentCart: CartItem[],
    definitions: AutomaticPromotionDefinition[]
): AutomaticPromotionResult => {
    if (!currentCart.length || !definitions.length) {
        return { cart: currentCart, appliedPromotionNames: [] };
    }

    const available = new Map<number, number>();
    currentCart.forEach(item => {
        if (!item.es_promocion) {
            available.set(item.idProductos, (available.get(item.idProductos) || 0) + item.quantity);
        }
    });

    const promotions = definitions
        .map(definition => ({ ...definition, items: normalizeRequirements(definition.items) }))
        .filter(definition =>
            definition.producto
            && !isProductOutOfStock(definition.producto)
            && definition.items.length > 0
        )
        .sort((a, b) =>
            b.items.reduce((total, item) => total + item.cantidad, 0)
            - a.items.reduce((total, item) => total + item.cantidad, 0)
        );

    const consumed = new Map<number, number>();
    const promotionCounts = new Map<number, number>();
    const appliedPromotionNames: string[] = [];

    promotions.forEach(promotion => {
        let times = Infinity;
        promotion.items.forEach(component => {
            const remaining = (available.get(component.id_producto) || 0)
                - (consumed.get(component.id_producto) || 0);
            times = Math.min(times, Math.floor(remaining / component.cantidad));
        });

        if (times < 1 || times === Infinity) return;

        promotion.items.forEach(component => {
            consumed.set(
                component.id_producto,
                (consumed.get(component.id_producto) || 0) + component.cantidad * times
            );
        });
        promotionCounts.set(promotion.id_promocion, times);
        appliedPromotionNames.push(promotion.nombre);
    });

    if (!promotionCounts.size) {
        return { cart: currentCart, appliedPromotionNames: [] };
    }

    const remainingToConsume = new Map(consumed);
    const nextCart: CartItem[] = [];

    currentCart.forEach(item => {
        if (item.es_promocion) {
            nextCart.push(item);
            return;
        }

        const pending = remainingToConsume.get(item.idProductos) || 0;
        const removed = Math.min(pending, item.quantity);
        remainingToConsume.set(item.idProductos, pending - removed);

        const remaining = item.quantity - removed;
        if (remaining > 0) nextCart.push({ ...item, quantity: remaining });
    });

    promotionCounts.forEach((quantity, promotionId) => {
        const definition = promotions.find(promotion => promotion.id_promocion === promotionId);
        if (!definition) return;

        const cartKey = String(promotionId);
        const existingIndex = nextCart.findIndex(item => item.cartKey === cartKey);
        if (existingIndex >= 0) {
            nextCart[existingIndex] = {
                ...nextCart[existingIndex],
                quantity: nextCart[existingIndex].quantity + quantity
            };
            return;
        }

        nextCart.push({ ...definition.producto, cartKey, quantity });
    });

    return { cart: nextCart, appliedPromotionNames };
};
